import { FC } from "react";
import { Box, Typography, Divider } from "@mui/material";
import { PositionInfo } from "../InterviewAssistant";
import colors from "../../../shared/constants/color";
import { textSM, textXS } from "../../../shared/constants";
import { ProjectStatusTag } from "../components/shared/ProjectStatusTag";
import { PriorityTag } from "../components/shared/PriorityTag";
import { Button } from "../../../components";
import { WorkingModelTag } from "../components/shared/WorkingModelTag";
import { MapPinIcon, UsersIcon } from "../../../shared/images/icons";
import ModalBase from "../../../components/ui/ModalBase";
import { useWiserTheme } from "../../../hooks/useWiserTheme";
import {
  getPositionModalGridSx,
  getSidebarIconFilter,
} from "../../../shared/theme/wiserThemeHelpers";
import {
  modalMaxHeightSx,
  modalHeaderRowSx,
  modalHeaderLeftSx,
  modalHeaderTitleRowSx,
  modalHeaderMetaRowSx,
  modalHeaderTagsSx,
  modalHeaderInfoRowSx,
  modalHeaderRightSx,
  getIcon14Style,
  getModalDividerSx,
} from "../style";

interface IProps {
  show: boolean;
  onClose: () => void;
  position: PositionInfo | null;
}

const PositionDescriptionModal: FC<IProps> = ({ show, onClose, position }) => {
  const { darkModeActive, tokens } = useWiserTheme();
  const iconFilter = getSidebarIconFilter(darkModeActive);

  if (!position) return null;

  const formatDate = (date: string | null) => {
    if (!date) return "";
    return new Date(date)
      .toLocaleString("tr-TR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
      .replace(",", "");
  };

  return (
    <ModalBase
      open={show}
      onClose={onClose}
      title="Pozisyon Açıklama"
      maxWidth="md"
      actions={
        <Button color={colors.deepBlue} variant="contained" onClick={onClose}>
          Kapat
        </Button>
      }
      sx={modalMaxHeightSx}
    >
      <>
        {/* Header */}
        <Box sx={modalHeaderRowSx}>
          {/* Header Left Side */}
          <Box sx={modalHeaderLeftSx}>
            <Box sx={modalHeaderTitleRowSx}>
              <Typography
                sx={textSM(
                  500,
                  darkModeActive ? tokens.textPrimary : colors.neutral.N800,
                )}
              >
                {(position.techGroup ?? []).map((tg) => tg.techGroupName).join(" - ")}
              </Typography>
              <WorkingModelTag workingModel={position.workingModelName || ""} />
            </Box>

            <Box sx={modalHeaderMetaRowSx}>
              {/* Client and Team */}
              <Typography
                sx={textXS(
                  400,
                  darkModeActive ? tokens.textSecondary : colors.neutral.N500,
                )}
              >
                {position.clientName?.toTitleCaseTR() || ""} ·{" "}
                {position.teamName?.toTitleCaseTR() || ""}
              </Typography>

              {/* Status and Priority Tags */}
              <Box sx={modalHeaderTagsSx}>
                <ProjectStatusTag status={position.statusName} />
                <PriorityTag priority={position.priorityId} />
              </Box>

              {/* Candidate Count and Location */}
              <Box sx={modalHeaderInfoRowSx}>
                <img
                  src={UsersIcon}
                  alt=""
                  style={getIcon14Style(iconFilter)}
                />
                <Typography
                sx={textXS(
                  400,
                  darkModeActive ? tokens.textSecondary : colors.neutral.N500,
                )}
              >
                  {position.headCount} Aday
                </Typography>
                <img
                  src={MapPinIcon}
                  alt=""
                  style={getIcon14Style(iconFilter)}
                />
                <Typography
                sx={textXS(
                  400,
                  darkModeActive ? tokens.textSecondary : colors.neutral.N500,
                )}
              >
                  {(position.location ?? []).map((loc) => loc.locationName).join(" - ")}
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Header Right Side */}
          <Box sx={modalHeaderRightSx}>
            <Typography
              sx={textXS(
                400,
                darkModeActive ? tokens.textSecondary : colors.neutral.N500,
              )}
            >
              Oluşturulma Tarihi: <b>{formatDate(position.createdAt)}</b>
            </Typography>
            <Typography
              sx={textXS(
                400,
                darkModeActive ? tokens.textSecondary : colors.neutral.N500,
              )}
            >
              <b>{position.createdBy}</b> tarafından oluşturuldu.
            </Typography>
          </Box>
        </Box>

        <Divider sx={getModalDividerSx(darkModeActive, tokens)} />

        <Box sx={getPositionModalGridSx(darkModeActive)}>
          <Typography>Müşteri Açıklama:</Typography>
          <Typography>{position.description || ""}</Typography>

          <Typography>IK Not:</Typography>
          <Typography>{position.detailDescription || ""}</Typography>
        </Box>
      </>
    </ModalBase>
  );
};

export default PositionDescriptionModal;
